// React Imports
import { useNavigate, useLocation } from "react-router";

// Local Components
import S from "./ProfileTabs.module.scss";

// External Libraries
import C from "classnames";
import { TabView, TabPanel } from "primereact/tabview";
import type { TabViewTabChangeEvent } from "primereact/tabview";

// Hooks and Helpers
import { buildRoute } from "sj-ui-toolkit/src/constants/routes";
import { sanitizeTestId } from "sj-ui-toolkit/src/utils/string";

export function ProfileTabs() {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs: { key: string; label: string; icon: string; path: string }[] = [
    {
      key: "achievements",
      label: "Achievements",
      icon: "pi pi-trophy",
      path: buildRoute("User.routes.achievements.route"),
    },
    {
      key: "certificates",
      label: "Certificates",
      icon: "pi pi-id-card",
      path: buildRoute("User.routes.certificates.route"),
    },
    {
      key: "history",
      label: "History",
      icon: "pi pi-history",
      path: buildRoute("User.routes.history.route"),
    },
    {
      key: "notes",
      label: "Notes",
      icon: "pi pi-clipboard",
      path: buildRoute("User.routes.notes.route"),
    },
    {
      key: "profile",
      label: "Profile",
      icon: "pi pi-user",
      path: buildRoute("User.routes.profile.route"),
    },
    {
      key: "settings",
      label: "Settings",
      icon: "pi pi-cog",
      path: buildRoute("User.routes.settings.route"),
    },
  ];

  const pathname = location.pathname;
  const matchingIndex = tabs.findIndex(tab => pathname === tab.path || pathname === tab.path + "/");
  const defaultIndex = tabs.findIndex(tab => tab.label === "Profile");
  const activeIndex = matchingIndex !== -1 ? matchingIndex : defaultIndex;

  const handleTabChange = (e: TabViewTabChangeEvent) => {
    const selectedTab = tabs[e.index];
    if (!selectedTab) return;
    navigate(selectedTab.path);
  };

  return (
    <div className={S.root} data-testid="profile-tabs">
      <TabView
        aria-label="Profile sections"
        activeIndex={activeIndex}
        onTabChange={handleTabChange}
        panelContainerClassName={S.hiddenPanels}
      >
        {tabs.map((tab, index) => (
          <TabPanel
            key={tab.key}
            header={
              <div
                className={C(S.tab, { [S.active!]: index === activeIndex })}
                data-testid={`profile-tab-${tab.key ?? sanitizeTestId(tab.label)}`}
              >
                <i className={tab.icon} aria-hidden="true" /> {tab.label}
              </div>
            }
          />
        ))}
      </TabView>
    </div>
  );
}
